/**
 * Wave 2.5 Block D: Map Layer Control
 * 
 * Provides base map selection and overlay toggles per SORA 2.5 requirements.
 * 
 * Base maps (radio, mutually exclusive):
 * - OpenStreetMap (DEFAULT, no key required)
 * - OpenTopoMap (no key)
 * - Satellite (no key, Esri)
 * - Stadia Maps (opt-in, requires API key)
 * - Mapy.cz (opt-in, requires API key)
 * 
 * The former overlay toggles (mission polygon, OpenAIP airspaces, population density, settlements,
 * land use) were removed with the mission-map unification (PR6): the mission polygon switch toggled
 * nothing, OpenAIP polygons are always empty, and the three population layers drew synthetic data
 * from endpoints that exist in neither backend.
 * 
 * Post-deployment fix (2026-05-28): Changed default from Stadia Outdoors to OpenStreetMap
 * because Stadia requires API key even for free tier (401 Unauthorized without key).
 */
import { useState, useEffect } from 'react';
import { Layers, Map, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';

// Storage key for persisting layer state
const LAYER_STATE_KEY = 'fleetora_map_layer_state';

// Base map definitions - ordered as displayed in UI
export const BASE_MAPS = {
  osm: {
    id: 'osm',
    name: { cs: 'OpenStreetMap', en: 'OpenStreetMap' },
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    requiresKey: false,
    maxZoom: 19
  },
  opentopomap: {
    id: 'opentopomap',
    name: { cs: 'OpenTopoMap', en: 'OpenTopoMap' },
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    requiresKey: false,
    maxZoom: 17
  },
  satellite: {
    id: 'satellite',
    name: { cs: 'Satelit', en: 'Satellite' },
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    requiresKey: false,
    maxZoom: 19
  },
  stadia_outdoors: {
    id: 'stadia_outdoors',
    name: { cs: 'Stadia Outdoors', en: 'Stadia Outdoors' },
    // URL with API key placeholder - key added at runtime
    urlTemplate: 'https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png?api_key={apikey}',
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    requiresKey: true,
    maxZoom: 20
  },
  mapycz: {
    id: 'mapycz',
    name: { cs: 'Mapy.cz', en: 'Mapy.cz' },
    // URL with API key placeholder - key added at runtime
    urlTemplate: 'https://api.mapy.cz/v1/maptiles/outdoor/256/{z}/{x}/{y}?apikey={apikey}',
    attribution: '&copy; <a href="https://www.mapy.cz">Mapy.cz</a> &copy; Seznam.cz',
    requiresKey: true,
    maxZoom: 19
  }
};

// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const stored = localStorage.getItem(LAYER_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to load map layer state:', e);
  }
  return null;
};

// Save state to localStorage
const persistState = (state) => {
  try {
    localStorage.setItem(LAYER_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to persist map layer state:', e);
  }
};

export const MapLayerControl = ({
  language = 'cs',
  mapyCzApiKey = null,
  stadiaApiKey = null,
  onBaseMapChange,
  currentBaseMap = 'osm',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedBaseMap, setSelectedBaseMap] = useState(currentBaseMap);

  // Load persisted state on mount
  useEffect(() => {
    const persisted = loadPersistedState();
    if (persisted) {
      // Migrate old stadia_outdoors default to osm if no key
      let baseMap = persisted.baseMap;
      if (baseMap === 'stadia_outdoors' && !stadiaApiKey) {
        baseMap = 'osm'; // Fallback to OSM if Stadia key not available
      }
      if (baseMap) {
        setSelectedBaseMap(baseMap);
        onBaseMapChange?.(baseMap);
      }
    }
  }, [stadiaApiKey]);

  // Translations
  const t = {
    title: language === 'cs' ? 'Vrstvy' : 'Layers',
    baseMaps: language === 'cs' ? 'Podkladové mapy' : 'Base Maps',
    requiresMapyCzApiKey: language === 'cs' 
      ? 'Vyžaduje Mapy.cz API klíč v nastavení organizace' 
      : 'Requires Mapy.cz API key in organization settings',
    requiresStadiaApiKey: language === 'cs'
      ? 'Vyžaduje API klíč — zaregistrujte se na stadiamaps.com'
      : 'Requires API key — register at stadiamaps.com'
  };

  // Check if a map requires key and if key is available
  const isMapDisabled = (mapId) => {
    const mapDef = BASE_MAPS[mapId];
    if (!mapDef.requiresKey) return false;
    if (mapId === 'mapycz') return !mapyCzApiKey;
    if (mapId === 'stadia_outdoors') return !stadiaApiKey;
    return false;
  };

  // Get tooltip message for disabled maps
  const getDisabledTooltip = (mapId) => {
    if (mapId === 'mapycz') return t.requiresMapyCzApiKey;
    if (mapId === 'stadia_outdoors') return t.requiresStadiaApiKey;
    return '';
  };

  const handleBaseMapChange = (mapId) => {
    // Check if map requires key and key is not available
    if (isMapDisabled(mapId)) {
      return; // Don't allow selection
    }
    
    setSelectedBaseMap(mapId);
    onBaseMapChange?.(mapId);
    
    // Persist state
    persistState({
      baseMap: mapId
    });
  };

  return (
    <div 
      className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{ minWidth: expanded ? '200px' : 'auto' }}
      data-testid="map-layer-control"
    >
      {/* Header - always visible */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 transition-colors"
        data-testid="layer-control-toggle"
      >
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">{t.title}</span>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-200 p-3 space-y-4">
          {/* Base Maps Section */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t.baseMaps}
            </Label>
            <div className="space-y-1">
              {Object.entries(BASE_MAPS).map(([id, mapDef]) => {
                const isDisabled = isMapDisabled(id);
                const isSelected = selectedBaseMap === id;
                
                return (
                  <TooltipProvider key={id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleBaseMapChange(id)}
                          disabled={isDisabled}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                            isSelected 
                              ? 'bg-blue-100 text-blue-800 font-medium' 
                              : isDisabled
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          data-testid={`basemap-${id}`}
                        >
                          <div className={`w-3 h-3 rounded-full border-2 ${
                            isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-400'
                          }`}>
                            {isSelected && <div className="w-1 h-1 bg-white rounded-full m-auto mt-0.5" />}
                          </div>
                          <Map className="h-3.5 w-3.5" />
                          <span className="flex-1 text-left">{mapDef.name[language]}</span>
                          {isDisabled && <Lock className="h-3 w-3 text-gray-400" />}
                        </button>
                      </TooltipTrigger>
                      {isDisabled && (
                        <TooltipContent side="left" className="max-w-xs">
                          <p className="text-xs">{getDisabledTooltip(id)}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapLayerControl;
